import { datadogLogs } from "@datadog/browser-logs";
import {
  PopupEvent,
  StandalonePopupType,
} from "@redotech/redo-onboarding-common/popup-events";
import { FeatureOnboardingType } from "@redotech/redo-onboarding-common/user-onboarding";

/**
 * Whether a manager-selected popup actually reached the merchant. Selection
 * alone is not enough: mobile, automated sessions, and some routes render null.
 */
export function isPopupVisibleToMerchant(input: {
  /** False when `index.tsx` rendered null — mobile, webdriver, or no match. */
  hasCard: boolean;
  routeSuppressed: boolean;
  managedReturnsTrialPreview: boolean;
  /** The type rendered, not the one selected: manual opens dismiss too. */
  renderedType: FeatureOnboardingType | undefined;
}): boolean {
  return (
    input.hasCard &&
    !input.routeSuppressed &&
    !input.managedReturnsTrialPreview &&
    !!input.renderedType &&
    input.renderedType !== FeatureOnboardingType.DISABLED
  );
}

/**
 * A completed popup cannot also have been dismissed. Accept handlers commonly
 * close the modal too, which would count every acceptance as a dismissal.
 */
export function shouldEmitPopupEvent(
  event: PopupEvent,
  acceptedSinceShown: ReadonlySet<FeatureOnboardingType>,
  type: FeatureOnboardingType,
): boolean {
  return !(event === PopupEvent.DISMISSED && acceptedSinceShown.has(type));
}

export type PopupType = FeatureOnboardingType | StandalonePopupType;

/** Seconds, not ms, so the Datadog measures read without a unit conversion. */
export interface PopupEventTiming {
  /** From the popup appearing to this event. */
  secondsSinceShown?: number;
  /** How much of the popup's video played, each second counted once. */
  videoSecondsWatched?: number;
}

/** Elapsed seconds from a `performance.now()` mark, to a tenth. */
export function secondsSince(
  startMs: number,
  nowMs: number = performance.now(),
): number {
  return Math.round((nowMs - startMs) / 100) / 10;
}

export function trackPopupEvent(
  event: PopupEvent,
  popupType: PopupType,
  teamId: string | undefined,
  timing: PopupEventTiming = {},
): void {
  // `event` is repeated into the attributes so Datadog can facet on `@event`
  // rather than matching on log message text. `popupType` is the facet the
  // admin popup-analytics query groups by.
  datadogLogs.logger.info(event, { event, popupType, teamId, ...timing });
}
